import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShareFormButton } from '@/components/ShareFormButton';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { FileSignature, Send, RotateCcw, CheckCircle, Download } from 'lucide-react';
import { generateMandatoPDF } from '@/lib/mandatoPdfUtils';
import SignatureCanvas from 'react-signature-canvas';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const MandatoSEPA = () => {
  const { toast } = useToast();
  const { perfil } = useAuth();
  const location = useLocation();
  const signatureRef = useRef<SignatureCanvas>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [submittedMandato, setSubmittedMandato] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    nombre_titular: '',
    direccion_calle: '',
    direccion_numero: '',
    codigo_postal: '',
    ciudad: '',
    provincia: '',
    nif_dni: '',
    iban: '',
  });

  // Pre-fill form data if coming from client detail
  useEffect(() => {
    if (location.state?.prefilledData) {
      setFormData(prev => ({
        ...prev,
        ...location.state.prefilledData
      }));
    }
  }, [location.state]);

  // Add cliente_id to the submission if coming from client
  const clienteId = location.state?.clienteId;

  const validateIBAN = (iban: string): boolean => {
    // Remover espacios y convertir a mayúsculas
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    
    // Verificar longitud básica para España (24 caracteres)
    if (cleanIban.length !== 24) return false;
    
    // Verificar que empiece con ES y seguido de números
    if (!cleanIban.match(/^ES\d{22}$/)) return false;
    
    // Algoritmo de verificación IBAN simplificado
    const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);
    const numericString = rearranged.replace(/[A-Z]/g, (char) => 
      (char.charCodeAt(0) - 55).toString()
    );
    
    // Verificar módulo 97
    let remainder = 0;
    for (let i = 0; i < numericString.length; i++) {
      remainder = (remainder * 10 + parseInt(numericString[i])) % 97;
    }
    
    return remainder === 1;
  };

  const formatIBAN = (value: string): string => {
    // Remover espacios y convertir a mayúsculas
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    // Añadir espacios cada 4 caracteres
    return cleaned.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'iban') {
      value = formatIBAN(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre_titular.trim()) {
      toast({
        title: "Error",
        description: "El nombre del titular es obligatorio.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.nif_dni.trim()) {
      toast({
        title: "Error", 
        description: "El NIF/DNI es obligatorio.",
        variant: "destructive",
      });
      return;
    }

    const cleanIban = formData.iban.replace(/\s/g, '');
    if (!validateIBAN(cleanIban)) {
      toast({
        title: "Error",
        description: "El IBAN introducido no es válido.",
        variant: "destructive",
      });
      return;
    }

    if (signatureRef.current?.isEmpty()) {
      toast({
        title: "Error",
        description: "La firma digital es obligatoria.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    // Obtener la firma como base64
    const firmaBase64 = signatureRef.current?.toDataURL();

    try {

      const mandatoData = {
        nombre_titular: formData.nombre_titular.trim(),
        direccion_calle: formData.direccion_calle.trim(),
        direccion_numero: formData.direccion_numero.trim(),
        codigo_postal: formData.codigo_postal.trim(),
        ciudad: formData.ciudad.trim(),
        provincia: formData.provincia.trim(),
        nif_dni: formData.nif_dni.trim(),
        iban: cleanIban,
        firma_digital: firmaBase64,
        empresa_id: perfil?.empresa_id,
        ...(clienteId && { cliente_id: clienteId })
      };

      const { error } = await supabase
        .from('mandatos_sepa')
        .insert(mandatoData);

      if (error) {
        throw error;
      }

      // Store submitted data for PDF generation
      const mandatoDataForPDF = {
        ...mandatoData,
        nombre_acreedor: 'QSeguridad',
        fecha_firma: new Date().toISOString(),
      };
      
      setSubmittedMandato(mandatoDataForPDF);
      setIsSubmitted(true);
      toast({
        title: "Mandato SEPA enviado",
        description: "Su autorización de domiciliación bancaria ha sido registrada correctamente.",
      });

    } catch (error) {
      console.error('Error al enviar mandato SEPA:', error);
      console.error('Form data:', formData);
      console.error('Clean IBAN:', cleanIban);
      console.error('Firma base64 length:', firmaBase64?.length);
      
      toast({
        title: "Error",
        description: `Error al enviar: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!submittedMandato) return;
    
    setIsDownloading(true);
    try {
      await generateMandatoPDF(submittedMandato);
      toast({
        title: "PDF descargado",
        description: "El mandato SEPA se ha descargado correctamente.",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "No se pudo generar el PDF. Inténtelo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="text-center">
            <CardContent className="pt-8 pb-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Mandato SEPA Enviado</h2>
              <p className="text-muted-foreground mb-6">
                Su autorización de domiciliación bancaria ha sido registrada correctamente.
                Recibirá una confirmación en breve.
              </p>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={handleDownloadPDF}
                  disabled={isDownloading}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  {isDownloading ? 'Generando PDF...' : 'Descargar PDF'}
                </Button>
                <Button
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    nombre_titular: '',
                    direccion_calle: '',
                    direccion_numero: '',
                    codigo_postal: '',
                    ciudad: '',
                    provincia: '',
                    nif_dni: '',
                    iban: '',
                  });
                  signatureRef.current?.clear();
                }}
                >
                  Enviar otro mandato
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header with title and share button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <FileSignature className="h-8 w-8 text-primary" />
              Mandato SEPA
            </h1>
            <p className="text-muted-foreground mt-2">
              Complete este formulario para autorizar la domiciliación bancaria de sus pagos mediante mandato SEPA.
            </p>
          </div>
          <div className="flex-shrink-0">
            <ShareFormButton 
              formType="mandato-sepa"
              title="Compartir Formulario Mandato SEPA"
              description="Formulario para crear un mandato SEPA"
            />
          </div>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">
              Autorización de Domiciliación Bancaria
            </CardTitle>
            <CardDescription>
              Todos los campos marcados con (*) son obligatorios.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Datos del titular */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Datos del titular de la cuenta</h3>
                
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="nombre_titular">Nombre completo del titular *</Label>
                    <Input
                      id="nombre_titular"
                      value={formData.nombre_titular}
                      onChange={(e) => handleInputChange('nombre_titular', e.target.value)}
                      placeholder="Nombre y apellidos completos"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="direccion_calle">Calle *</Label>
                      <Input
                        id="direccion_calle"
                        value={formData.direccion_calle}
                        onChange={(e) => handleInputChange('direccion_calle', e.target.value)}
                        placeholder="Nombre de la calle"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="direccion_numero">Número *</Label>
                      <Input
                        id="direccion_numero"
                        value={formData.direccion_numero}
                        onChange={(e) => handleInputChange('direccion_numero', e.target.value)}
                        placeholder="Nº"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="codigo_postal">Código Postal *</Label>
                      <Input
                        id="codigo_postal"
                        value={formData.codigo_postal}
                        onChange={(e) => handleInputChange('codigo_postal', e.target.value)}
                        placeholder="28001"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="ciudad">Ciudad *</Label>
                      <Input
                        id="ciudad"
                        value={formData.ciudad}
                        onChange={(e) => handleInputChange('ciudad', e.target.value)}
                        placeholder="Madrid"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="provincia">Provincia *</Label>
                      <Input
                        id="provincia"
                        value={formData.provincia}
                        onChange={(e) => handleInputChange('provincia', e.target.value)}
                        placeholder="Madrid"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nif_dni">NIF / DNI *</Label>
                      <Input
                        id="nif_dni"
                        value={formData.nif_dni}
                        onChange={(e) => handleInputChange('nif_dni', e.target.value)}
                        placeholder="12345678Z"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="iban">IBAN *</Label>
                      <Input
                        id="iban"
                        value={formData.iban}
                        onChange={(e) => handleInputChange('iban', e.target.value)}
                        placeholder="ES91 2100 0418 4502 0005 1332"
                        maxLength={29}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Formato: ES91 2100 0418 4502 0005 1332
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Datos del acreedor */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Datos del acreedor</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nombre_acreedor">Nombre del acreedor</Label>
                    <Input
                      id="nombre_acreedor"
                      value="QSeguridad"
                      disabled
                      className="bg-muted"
                    />
                  </div>
                  <div>
                    <Label htmlFor="identificador_acreedor">Identificador del acreedor</Label>
                    <Input
                      id="identificador_acreedor"
                      value="Se asignará automáticamente"
                      disabled
                      className="bg-muted"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="fecha_firma">Fecha de firma</Label>
                  <Input
                    id="fecha_firma"
                    value={new Date().toLocaleDateString('es-ES')}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>

              {/* Firma digital */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Firma digital *</h3>
                <p className="text-sm text-muted-foreground">
                  Firme en el recuadro inferior usando su dedo (móvil/tablet) o el ratón (ordenador)
                </p>
                
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'signature-canvas w-full h-32 border rounded',
                      style: { backgroundColor: 'white' }
                    }}
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearSignature}
                      className="flex items-center gap-2"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Limpiar firma
                    </Button>
                  </div>
                </div>
              </div>

              {/* Información legal */}
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-semibold">Información y consentimiento</h4>
                <p className="text-sm text-muted-foreground">
                  Al firmar este mandato, autoriza a QSeguridad a enviar instrucciones a su banco para adeudar su cuenta 
                  y a su banco para efectuar adeudos en su cuenta de conformidad con las instrucciones de QSeguridad.
                </p>
                <p className="text-sm text-muted-foreground">
                  Como parte de sus derechos, está legitimado al reembolso por su banco bajo los términos y condiciones 
                  del contrato suscrito con la entidad bancaria. La solicitud de reembolso deberá efectuarse dentro de las 
                  ocho semanas que siguen a la fecha de adeudo en cuenta.
                </p>
              </div>

              {/* Botón de envío */}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2 min-w-[150px]"
                >
                  <Send className="h-4 w-4" />
                  {isSubmitting ? 'Enviando...' : 'Enviar mandato'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MandatoSEPA;